import Head from 'next/head'
import { usePathname } from 'next/navigation'

import { AmplitudeScript } from '../../../app/_components/chrome/scripts/AmplitudeScript'
import { AttentiveScript } from '../../../app/_components/chrome/scripts/AttentiveScript'
import { DataDogRumScript } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { FriendBuyScript } from '../../../app/_components/chrome/scripts/FriendBuyScript'
import { GtmScript } from '../../../app/_components/chrome/scripts/GtmScript'
import { NostoScript } from '../../../app/_components/chrome/scripts/NostoScript'
import { OneTrustScript } from '../../../app/_components/chrome/scripts/OneTrustScript'
import { SienaChatScript } from '../../../app/_components/chrome/scripts/SienaChatScript'
import { useFixturesContext } from '../../../brunswick/containers/fixtures'
import { useAccentColor } from '../../../containers/AccentColorContainer'
import { embroideryFontFaces } from '../../../lib/embroideryFonts'
import { figsFont } from '../../../lib/font'

/**
 * > `next/script` must not be placed in either a `next/head` component or in `pages/_document.js`.
 * - https://nextjs.org/docs/basic-features/script
 *
 * We want one `meta[name="viewport"]` with `initial-scale=1`. It can't be set in MagnoliaDocument.
 * For `{ Head } from 'next/head` (but not `{ Head } from 'next/document'`, as in MagnoliaDocument)
 * `defaultHead` in https://github.com/vercel/next.js/blob/canary/packages/next/shared/lib/head.tsx
 * will push `<meta name="viewport" content="width=device-width" />` and `<meta charSet="utf-8" />`
 * unless you define them. However, `{ Head } from 'next/document'` does not inject default values.
 * Setting `meta[name="viewport"]` in MagnoliaDocument results in a duplicate tag with a bad value.
 *
 * Tested in `magnolia/playwright/e2e/smoke/home.spec.ts`.
 */
export const ContextualHead = () => {
  const { accentColor } = useAccentColor()
  const { promoBar } = useFixturesContext()
  const pathname = usePathname()

  return (
    <>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1' key='viewport' />
        <meta
          name='theme-color'
          content={accentColor ?? promoBar?.backgroundColor ?? '#282828'}
          key='theme-color'
        />
        <style
          type='text/css'
          dangerouslySetInnerHTML={{ __html: `@charset 'utf-8'; ${figsFont}` }}
          key='figs-font-faces'
        />
        <style
          type='text/css'
          dangerouslySetInnerHTML={{ __html: embroideryFontFaces }}
          key='embroidery-font-faces'
        />
      </Head>

      <OneTrustScript />
      <DataDogRumScript />
      <AmplitudeScript sessionReplay />
      <NostoScript />
      <FriendBuyScript />
      <AttentiveScript />
      <GtmScript />
      <SienaChatScript shiftWidgetUpMobile={pathname?.includes('/kits')} />
    </>
  )
}
